import React, { useState, useEffect } from "react";
import { RemoteMonitoringModalContainer, RemoteMonitoringModalContent } from "../../../Constants/StyledComponents";
import { useModalProvider } from "../../../Providers/ModalProvider";
import { useBackendLinksProvider } from "../../../Providers/BackendLinksProvider.tsx";
import { useEndpointProvider } from "../../../Providers/EndpointProvider";
import RMSelectGateway from "../../../Pages/RemoteMonitoring/ModalView/RMSelectGateway";
import RMAddPatient from "../../../Pages/RemoteMonitoring/ModalView/RMAddPatient";
import RMSelectPatient from "../../../Pages/RemoteMonitoring/ModalView/RMSelectPatient";
import RMGatewayMonitoring from "../../../Pages/RemoteMonitoring/ModalView/RMGatewayMonitoring";
import StartMonitoringModal from "../../../Pages/RemoteMonitoring/components/Modals/StartMonitoringModal";
import { useEnvironmentVariablesProvider } from "../../../Providers/EnvironmentVariablesProvider.tsx";
import Header from "../../../Pages/RemoteMonitoring/components/Header";
import ModalFooter from "./RemoteMonitoringModalFooter";
import { MobergTheme } from "../../../Moberg";
import { MdArrowBack } from "react-icons/md";
import { useAuthProvider } from "../../../Providers/AuthProvider";
import MobergSnackbar from "../../../Components/MobergSnackbar/MobergSnackbar";
import { 
    RM_STEPS, 
    ACTIVE_MONITORING_STATUSES, 
    URL_PARAMS, 
    MODAL_TYPE 
} from "../../../Pages/RemoteMonitoring/constants";

// Keeping STEPS for backward compatibility within this file
const STEPS = RM_STEPS;



const RemoteMonitoringModal = () => {
    const { environmentVariables } = useEnvironmentVariablesProvider();
    const { close } = useModalProvider();
    const { LINKS } = useBackendLinksProvider();
    const endpointProvider = useEndpointProvider();
    const authProvider = useAuthProvider();
    const [currentStep, setCurrentStep] = useState(STEPS.SELECT_GATEWAY);
    const [gatewayId, setGatewayId] = useState(null);
    const [gatewayInfo, setGatewayInfo] = useState(null);
    const [patientId, setPatientId] = useState(null);
    const [patientDetails, setPatientDetails] = useState({
        mrn: null,
        firstName: null,
        lastName: null
    });
    const [componentHandlers, setComponentHandlers] = useState({
        onNext: null,
        onBack: null,
        isNextDisabled: true
    });
    const [showStartMonitoringModal, setShowStartMonitoringModal] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [error, setError] = useState(null);

    // Initialize from URL parameters on mount
    useEffect(() => {
        const initializeFromUrl = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const urlStep = urlParams.get(URL_PARAMS.STEP);
            const urlGatewayId = urlParams.get(URL_PARAMS.GATEWAY_ID);

            if (urlStep && Object.values(STEPS).includes(urlStep)) {
                if (urlGatewayId) {
                    try {
                        const res = await endpointProvider.get(LINKS.AGGREGATOR.GET_GATEWAY_INFO, {
                            gateway_id: parseInt(urlGatewayId, 10)
                        });
                        
                        setGatewayId(urlGatewayId);
                        setGatewayInfo(res);
                        setCurrentStep(urlStep);
                    } catch (err) {
                        console.error("Error fetching gateway info from URL:", err);
                        setError('Unable to load device information from the link. Please select a device below.');
                        setCurrentStep(STEPS.SELECT_GATEWAY);
                    }
                } else {
                    setCurrentStep(urlStep);
                }
            }
            
            setIsInitialized(true);
        };

        if (!isInitialized) {
            initializeFromUrl();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInitialized]);

    // Sync URL with current step and gatewayId
    useEffect(() => {
        if (!isInitialized) return;

        const urlParams = new URLSearchParams(window.location.search);
        
        // Always set the modal and step params
        urlParams.set(URL_PARAMS.MODAL, MODAL_TYPE.REMOTE_MONITORING);
        urlParams.set(URL_PARAMS.STEP, currentStep);
        
        if (gatewayId) {
            urlParams.set(URL_PARAMS.GATEWAY_ID, gatewayId);
        } else {
            urlParams.delete(URL_PARAMS.GATEWAY_ID);
        }
        
        window.history.replaceState(null, '', `${window.location.pathname}?${urlParams.toString()}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, gatewayId, isInitialized]);

    // Navigation handlers
    const handleSelectGateway = async (selectedGatewayId, selectedGatewayInfo) => {
        setGatewayId(selectedGatewayId);
        setGatewayInfo(selectedGatewayInfo);
        
        const gwStatus = selectedGatewayInfo?.gateway?.status;
        const normalizedStatus = gwStatus?.toUpperCase();
        
        const hasActiveMonitoring = normalizedStatus && ACTIVE_MONITORING_STATUSES.includes(normalizedStatus);
        
        if (hasActiveMonitoring) {
            setCurrentStep(STEPS.MONITORING);
        } else {
            setCurrentStep(STEPS.ADD_PATIENT);
        }
    };

    const handleBackToSelectGateway = () => {
        setGatewayId(null);
        setGatewayInfo(null);
        setPatientId(null);
        setPatientDetails({ mrn: null, firstName: null, lastName: null });
        setCurrentStep(STEPS.SELECT_GATEWAY);
    };

    const handleNavigateToSelectPatient = () => {
        setCurrentStep(STEPS.SELECT_PATIENT);
    };

    const handleAddPatient = (mrn, firstName, lastName) => {
        setPatientId(mrn);
        const newPatientDetails = { mrn, firstName, lastName };
        setPatientDetails(newPatientDetails);
        // Show StartMonitoringModal overlay for confirmation (prevents PHI in URL)
        setShowStartMonitoringModal(true);
    };

    const handleSelectPatient = (selectedPatientId, patientName) => {
        setPatientId(selectedPatientId);
        // Parse patient name if provided (format: "FirstName LastName" or just one name)
        let firstName = null;
        let lastName = null;
        if (patientName && patientName !== "Unknown") {
            const nameParts = patientName.trim().split(/\s+/);
            if (nameParts.length === 1) {
                firstName = nameParts[0];
            } else if (nameParts.length >= 2) {
                firstName = nameParts[0];
                lastName = nameParts.slice(1).join(" ");
            }
        }
        setPatientDetails({ mrn: selectedPatientId, firstName, lastName });
        // Show StartMonitoringModal overlay for confirmation (prevents PHI in URL)
        setShowStartMonitoringModal(true);
    };

    const handleBackToAddPatient = () => {
        setCurrentStep(STEPS.ADD_PATIENT);
    };

    const handleBackToSelectPatient = () => {
        setCurrentStep(STEPS.SELECT_PATIENT);
    };

    const handleConfirmMonitoring = () => {
        setShowStartMonitoringModal(false);
        setCurrentStep(STEPS.MONITORING);
    };

    const handleCancelMonitoring = () => {
        setShowStartMonitoringModal(false);
    };

    const handleStopMonitoring = () => {
        handleBackToSelectGateway();
    };

    const handleClose = () => {
        // Clear modal-related URL params (afterClose in DataVisualize will handle this)
        close();
    };

    // Get header props based on current step
    const getHeaderProps = () => {
        switch (currentStep) {
            case STEPS.SELECT_GATEWAY:
                return {
                    title: "Select a MCP Device",
                    onClose: handleClose
                };
            case STEPS.ADD_PATIENT:
                return {
                    title: "Assign a patient",
                    onClose: handleClose
                };
            case STEPS.SELECT_PATIENT:
                return {
                    title: "Select a Patient",
                    onClose: handleClose
                };
            case STEPS.MONITORING:
                return {
                    title: "MCP Device Information",
                    onClose: handleClose
                };
            default:
                return {
                    title: "",
                    onClose: handleClose
                };
        }
    };

    // Get footer props based on current step
    const getFooterProps = () => {
        switch (currentStep) {
            case STEPS.SELECT_GATEWAY:
                return {
                    showBackButton: false,
                    showNextButton: false  // Hide Next button - View/Select buttons in table handle navigation
                };
            case STEPS.ADD_PATIENT:
                return {
                    showBackButton: true,
                    showNextButton: true,
                    onBack: handleBackToSelectGateway,
                    onNext: componentHandlers.onNext || (() => {}),
                    isNextDisabled: componentHandlers.isNextDisabled,
                    nextButtonText: "Next"
                };
            case STEPS.SELECT_PATIENT:
                return {
                    showBackButton: true,
                    showNextButton: true,
                    onBack: handleBackToAddPatient,
                    onNext: componentHandlers.onNext || (() => {}),
                    isNextDisabled: componentHandlers.isNextDisabled,
                    nextButtonText: "Next"
                };
            case STEPS.MONITORING:
                return {
                    showBackButton: false,
                    showCustomLeftButton: true,
                    customLeftButtonText: "MCP Devices",
                    customLeftButtonIcon: <MdArrowBack size={18} />,
                    onCustomLeftButton: handleBackToSelectGateway,
                    showToggleButton: true,
                    showNextButton: true,
                    onToggle: componentHandlers.onToggle || (() => {}),
                    onNext: componentHandlers.onStop || handleStopMonitoring,
                    toggleButtonText: componentHandlers.toggleButtonText || "Pause",
                    isToggleDisabled: componentHandlers.isToggleDisabled || false,
                    isNextDisabled: componentHandlers.isStopDisabled || false,
                    nextButtonText: "Stop",
                    nextButtonTheme: MobergTheme.RED
                };
            default:
                return {
                    showBackButton: false,
                    showNextButton: false
                };
        }
    };

    // Render current step
    const renderStep = () => {
        switch (currentStep) {
            case STEPS.SELECT_GATEWAY:
                return (
                    <RMSelectGateway 
                        setNavPage={() => {}} 
                        onNext={handleSelectGateway}
                        isModal={true}
                        onClose={handleClose}
                    />
                );
            
            case STEPS.ADD_PATIENT:
                return (
                    <RMAddPatient 
                        setNavPage={() => {}} 
                        gatewayId={gatewayId}
                        gatewayInfo={gatewayInfo}
                        onNext={handleAddPatient}
                        onBack={handleBackToSelectGateway}
                        onNavigateToSelectPatient={handleNavigateToSelectPatient}
                        isModal={true}
                        onClose={handleClose}
                        exposeHandlers={setComponentHandlers}
                    />
                );
            
            case STEPS.SELECT_PATIENT:
                return (
                    <RMSelectPatient 
                        setNavPage={() => {}} 
                        gatewayId={gatewayId}
                        onNext={handleSelectPatient}
                        onBack={handleBackToAddPatient}
                        isModal={true}
                        onClose={handleClose}
                        exposeHandlers={setComponentHandlers}
                        hideHeader={true}
                    />
                );
            
            case STEPS.MONITORING:
                return (
                    <RMGatewayMonitoring 
                        setNavPage={() => {}} 
                        gatewayId={gatewayId}
                        onStop={handleStopMonitoring}
                        isModal={true}
                        onClose={handleClose}
                        exposeHandlers={setComponentHandlers}
                    />
                );
            
            default:
                return null;
        }
    };

    return (
        <RemoteMonitoringModalContainer>
            <Header {...getHeaderProps()} />
            <RemoteMonitoringModalContent>
                {renderStep()}
            </RemoteMonitoringModalContent>
            <ModalFooter {...getFooterProps()} />
            
            {/* Start Monitoring Confirmation Modal */}
            <StartMonitoringModal
                isOpen={showStartMonitoringModal}
                gatewayId={gatewayId}
                gatewayInfo={gatewayInfo}
                patientId={patientId}
                patientDetails={patientDetails}
                onConfirm={handleConfirmMonitoring}
                onCancel={handleCancelMonitoring}
                endpointProvider={endpointProvider}
                LINKS={LINKS}
                authProvider={authProvider}
                siteId={environmentVariables.SITE_ID}
                projectId={environmentVariables.PROJECT_ID}
            />
            
            {/* Error Notification */}
            <MobergSnackbar
                open={!!error}
                onClose={() => setError(null)}
                message={error}
                severity="error"
            />
        </RemoteMonitoringModalContainer>
    );
};

export default RemoteMonitoringModal;

